import Linkify from '../utilities/Linkify';
import styles from './style.module.css';

// Step 30: the grant, organised by type of information as the owner's
// picture shows — title, subtitle, three boxes (award, deadline, applicant
// type), the description, three green chips, then the Pre-Application box,
// the Website links box and a More details box that keeps every labelled
// line the old view had. Links are drawn with Linkify (no raw HTML).

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday',
                  'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November',
                'December'];

// The DEADLINE box: "Jun 5, 2026 · 11:59 PM GMT". Stored deadlines are
// midnight-end UTC, so the zone printed is GMT (the true zone).
function formatDeadlineBox(value) {
  const time = Date.parse(value);
  if (isNaN(time)) return String(value);
  const d = new Date(time);
  let hours = d.getUTCHours();
  const marker = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  const minutes = String(d.getUTCMinutes()).padStart(2, '0');
  return `${MONTHS_SHORT[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}` +
         ` · ${hours}:${minutes} ${marker} GMT`;
}

// The owner's long date format (2026-08-21), kept for the More details
// line: "Sunday, September 14, 2025 11:59 Pm GMT".
function formatStoredDate(value) {
  const time = Date.parse(value);
  if (isNaN(time)) return String(value);
  const d = new Date(time);
  let hours = d.getUTCHours();
  const marker = hours >= 12 ? 'Pm' : 'Am';
  hours = hours % 12 || 12;
  const minutes = String(d.getUTCMinutes()).padStart(2, '0');
  return `${WEEKDAYS[d.getUTCDay()]}, ${MONTHS[d.getUTCMonth()]} ` +
         `${d.getUTCDate()}, ${d.getUTCFullYear()} ${hours}:${minutes} ` +
         `${marker} GMT`;
}

// Full dollars with commas, as the picture shows "$25,000".
function dollars(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return `$${value}`;
  return `$${Math.round(n).toLocaleString('en-US')}`;
}
function formatAward(min, max) {
  const hasMin = min !== '' && min != null && Number.isFinite(Number(min));
  const hasMax = max !== '' && max != null && Number.isFinite(Number(max));
  if (hasMin && hasMax) {
    return Number(min) === Number(max) ? dollars(min) : `${dollars(min)} – ${dollars(max)}`;
  }
  if (hasMin) return `From ${dollars(min)}`;
  if (hasMax) return `Up to ${dollars(max)}`;
  return 'Not stated';
}

// Helper to safely convert string or array to array
const toArray = (value, delimiter = ',') => {
  if (!value) return [];
  return Array.isArray(value)
    ? value.map((v) => String(v).trim()).filter(Boolean)
    : typeof value === 'string'
    ? value.split(delimiter).map((v) => v.trim()).filter(Boolean)
    : [];
};

// "RFP & Supporting Documents" is a list of {text, url}; a plain string or
// a list of strings is tolerated.
function toDocuments(value) {
  if (!value) return [];
  const list = Array.isArray(value) ? value : [value];
  return list
    .map((item) => {
      if (item && typeof item === 'object') {
        return { text: item.text ? String(item.text) : '', url: item.url ? String(item.url) : '' };
      }
      if (typeof item === 'string') {
        return /^https?:\/\//i.test(item) ? { text: '', url: item } : { text: item, url: '' };
      }
      return null;
    })
    .filter((d) => d && (d.text || d.url));
}

function ExternalLink({ href, className }) {
  if (!href) return null;
  const target = /^https?:\/\//i.test(href) ? href : `https://${href}`;
  return (
    <a href={target} target="_blank" rel="noopener noreferrer" className={className}>
      {href}
    </a>
  );
}

export default function DisplayedGrant({ selectedHit }) {
  const hit = selectedHit || {};

  // Step 14: the real category list where a record has one; the old
  // comma-split text otherwise.
  const categories =
    Array.isArray(hit['Categories List']) && hit['Categories List'].length > 0
      ? hit['Categories List']
      : toArray(hit.Categories);
  const geoFocus = toArray(hit['Geographic Focus'], ':');
  const applicantTypes = toArray(hit['Applicant Types']);
  const description = hit['Grant Description'] || hit.Description;
  const documents = toDocuments(hit['RFP & Supporting Documents']);

  const subtitle = [hit['Agency'], categories.join(', '), geoFocus.join(' > ')]
    .map((part) => (part == null ? '' : String(part).trim()))
    .filter(Boolean)
    .join(' · ');

  const deadlineType = String(hit['Deadline Type'] || '');
  let deadlineText = '';
  if (deadlineType === 'Ongoing') {
    deadlineText = 'Apply anytime';
  } else if (hit['Deadline Date']) {
    deadlineText = formatDeadlineBox(hit['Deadline Date']);
    if (deadlineType === 'Recurring' && hit['Deadline Cycle']) {
      deadlineText += ` · ${hit['Deadline Cycle']}`;
    }
  } else if (deadlineType === 'Recurring' && hit['Deadline Cycle']) {
    deadlineText = String(hit['Deadline Cycle']);
  }

  const websiteLinks = [];
  const addLink = (url) => {
    const clean = url == null ? '' : String(url).trim();
    if (clean && !websiteLinks.includes(clean)) websiteLinks.push(clean);
  };
  addLink(hit['Application URL']);
  addLink(hit['URL for Full Text']);
  documents.forEach((d) => addLink(d.url));

  // Owner's safety rule (2026-08-21): every line that can hold information
  // always shows its label — blank after it when this grant has none.
  const details = [
    ['Item ID', hit['GrantWatch ID#']],
    ['Grant Name', hit['Grant Name']],
    ['Due Date', hit['Conf. Date']],
    ['Important Dates', hit['Important Dates']],
    ['Deadline Date', hit['Deadline Date'] ? formatStoredDate(hit['Deadline Date']) : ''],
    ['Contact Information', hit['Contact Information']],
    ['Eligibility', hit['Additional Eligibility Criteria']],
    ['Funding', hit['Additional Funding Information']],
    ['Estimated Grant Size', hit['Estimated Size of Grant']],
    ['Number of Grants', hit['Number of Grants']],
    ['Grantor Events', hit['Grantor Events']],
    ['Pre-proposal Conference', hit['Pre-proposal Conference']],
    ['Term of Contract', hit['Term of Contract']],
    ['Agency Type', hit['Agency']],
    ['Geographic Focus', geoFocus.join(' > ')],
  ];

  return (
    <div className={styles.grant}>
      <h1 className={styles.grantTitle}>{hit['Funding Source']}</h1>
      <p className={styles.grantSubtitle}>{subtitle}</p>

      {/* the three boxes */}
      <div className={styles.statRow}>
        <div className={styles.statBox}>
          <div className={styles.statLabel}>Award amount</div>
          <div className={styles.statValue}>{formatAward(hit['Amount Min'], hit['Amount Max'])}</div>
        </div>
        <div className={styles.statBox}>
          <div className={styles.statLabel}>Deadline</div>
          <div className={styles.statValue}>{deadlineText}</div>
        </div>
        <div className={styles.statBox}>
          <div className={styles.statLabel}>Applicant type</div>
          <div className={styles.statValue}>{applicantTypes.join(' · ')}</div>
        </div>
      </div>

      {/* the description, links clickable */}
      <p className={styles.grantDescription}>
        <Linkify text={description} />
      </p>

      {/* Step 30: the three green chips are drawn on every grant, exactly as
          the picture — no data exists for them yet; the owner decided. */}
      <div className={styles.chipRow}>
        <span className={styles.chip}>✓ Eligibility matches this client</span>
        <span className={styles.chip}>✓ No LOI required</span>
        <span className={styles.chip}>✓ Reapplication allowed</span>
      </div>

      {/* Box 1: Pre-Application Information + RFP & Supporting Documents */}
      <div className={styles.infoBox}>
        <div className={styles.infoHeading}>Pre-Application Information:</div>
        <div className={styles.infoQuote}>
          {hit['Pre-Application Information']
            ? <>"<Linkify text={hit['Pre-Application Information']} />"</>
            : ''}
        </div>

        <div className={styles.infoDocs}>
          <span className={styles.infoHeading}>RFP &amp; Supporting Documents:</span>
          {documents.map((doc, i) => (
            <div key={i} className={styles.infoDoc}>
              {doc.text ? <div className={styles.infoQuote}>"{doc.text}"</div> : null}
              {doc.url ? (
                <div className={styles.infoUrl}>
                  url: "<ExternalLink href={doc.url} className={styles.link} />"
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </div>

      {/* Box 2: Website links */}
      <div className={styles.infoBox}>
        <div className={styles.boxLabel}>Website links</div>
        {websiteLinks.map((url) => (
          <div key={url} className={styles.linkLine}>
            <ExternalLink href={url} className={styles.linkBold} />
          </div>
        ))}
      </div>

      {/* Box 3: More details — every remaining labelled line of the old view */}
      <div className={styles.infoBox}>
        <div className={styles.boxLabel}>More details</div>
        {details.map(([label, value]) => (
          <div key={label} className={styles.detailLine}>
            <span className={styles.detailLabel}>{label}: </span>
            <span className={styles.detailValue}>
              <Linkify text={value == null ? '' : String(value)} />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
